import {
  Scene,
  PageContent,
  PageContainer,
  MinimizeButton,
  ButtonsContainer,
  FamilyDescription,
  // FamilyInfoContainer,
  FamilyMembersContainer
} from './Overview.styles';

// import Content from './content';
import API from '@/services/api';
import { connect } from 'react-redux';
import { FamilyName } from './FamilyName';
import { useNavigateQuery } from '@/hooks';
import Button from '@CS/components/Button';
import OverviewScene from './OverviewScene';
import { isStaging } from '@/utils/Location';

import type { Family } from '@/services/api/types';
import PopUpDeleteMember from './PopUpDeleteMember';
import { useRef, useState, useEffect } from 'react';
import FamilyMember from '@/components/FamilyMember';
import { getUserFamily } from '@/redux/user/selectors';
import PopUpErrorAddMember from './PopUpErrorAddMember';
import { /* useTranslation, */ useDataLayer } from '@CS/hooks';
import PopUpErrorDeleteMember from './PopUpErrorDeleteMember';
import Minimize from '@/assets/images/icons/minimize.svg?component';
import { addPlexureTags, removePlexureTags } from '@/services/app/mcd';
import { ErrorPopup } from '@/components/Popups';
import { getCampaign } from '@/redux/campaign/selectors';
import type { CampaignState } from '@/redux/campaign/types';

type OverviewProps = {
  campaign: CampaignState;
  onLoad: () => void;
  family?: Family;
};

const Overview = ({ family, onLoad, campaign }: OverviewProps) => {
  const navigate = useNavigateQuery();
  const scene = useRef<OverviewScene>();
  const canvas = useRef<HTMLCanvasElement>(null);
  const [minimizeUp, setMinimizeUp] = useState(false);

  const onCloseDeleteMember = () => setDeleteMemberId('');
  const [deleteMemberId, setDeleteMemberId] = useState('');
  const [errorAddMember, setErrorAddMember] = useState(false);
  const [serverNameError, setServerNameError] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);

  const updateName = async (name: string) => {
    try {
      await API.updateFamily(name);
      setIsEditingName(false);
    } catch (error: any) {
      const message = error.response?.data?.message || '';
      const isAbusiveWordError = message.toLowerCase().includes('abusive');
      const { name_profinaty } = campaign.translations;

      if (isAbusiveWordError) {
        setServerNameError(name_profinaty);
        setIsEditingName(true);
      } else {
        setServerNameError('There was an error, please try again later');
        setIsEditingName(true);
      }
    }
  };
  const onCloseErrorAddMember = () => setErrorAddMember(false);
  // const { description, finish, add } = useTranslation(Content);

  const [errorDeleteMember, setErrorDeleteMember] = useState(false);
  const onCloseErrorDeleteMember = () => setErrorDeleteMember(false);
  const onEditMember = (id: string | number) => navigate(`/editor?id=${id}`);

  useDataLayer({
    event: 'page_view',
    eventProperties: {
      page_virtual_path: '/fam-mode-2/family-avatars',
      page_type: 'fam-mode-2'
    }
  });

  const onMinimize = () => setMinimizeUp(!minimizeUp);

  const onSetDeleteMember = (id: string) => {
    if (!family) return;

    family.avatars.length > 1
      ? setDeleteMemberId(id)
      : setErrorDeleteMember(true);
  };

  const onDeleteMember = async () => {
    if (!family) return;
    const { length } = family.avatars;
    const id = Math.min(53 + length, 60);

    await API.deleteAvatar(deleteMemberId);
    scene.current?.removeAvatar(deleteMemberId);

    removePlexureTags([`IT_Custom_${id}`])
      .then(() => {
        const id = Math.min(53 + length - 1, 60);

        addPlexureTags([`IT_Custom_${id}`])
          .then(() => setDeleteMemberId(''))
          .catch((error) => console.error(error));
      })
      .catch((error) => console.error(error));
  };

  const onAddMember = () => {
    if (!family) return;

    family.avatars.length === 8.0
      ? setErrorAddMember(true)
      : navigate('/editor');
  };

  useEffect(() => {
    if (!family || scene.current) return;

    if (!family.avatars.length) {
      return navigate('/editor');
    }

    scene.current = new OverviewScene(
      canvas.current as HTMLCanvasElement,
      family.baseModelUrl,
      family.avatars
    );

    scene.current.onLoad = onLoad;
  }, [family]);

  useEffect(() => {
    scene.current?.dispose.bind(scene.current);
  }, []);

  useDataLayer({
    event: 'page_view',
    eventProperties: {
      page_virtual_path: '/family-mode/klaar/webview',
      page_type: 'family-mode'
    }
  });

  return (
    <>
      <PageContainer>
        <Scene ref={canvas} />

        {family && (
          <PageContent data-testid="page-container">
            <MinimizeButton
              hidden={isEditingName}
              onClick={onMinimize}
              up={minimizeUp}
              variant="icon"
            >
              <Minimize />
            </MinimizeButton>

            {/* <FamilyInfoContainer data-testid="info-container"> */}
            <FamilyName
              onEditEnd={() => setIsEditingName(false)}
              onSubmit={updateName}
              name={family.name}
              onEdit={() => setIsEditingName(true)}
              isEditing={isEditingName}
            />

            <FamilyDescription>{campaign.translations.family_members_overview}</FamilyDescription>
            {/* </FamilyInfoContainer> */}

            <FamilyMembersContainer
              data-testid="members-container"
              minimized={minimizeUp}
            >
              {family.avatars.map(
                ({ favoriteColor, headshotImageUrl, name, id }, d) => (
                  <FamilyMember
                    backgroundColor={favoriteColor}
                    previewImage={headshotImageUrl}
                    onDelete={onSetDeleteMember}
                    onEdit={onEditMember}
                    key={`${d}${name}`}
                    name={name}
                    id={id}
                  />
                )
              )}
            </FamilyMembersContainer>
          </PageContent>
        )}

        <ButtonsContainer>
          <Button onClick={onAddMember} variant="secondary" text={campaign.translations.add_member} />

          <Button
            text={campaign.translations.complete_members}
            link={
              isStaging
                ? 'https://frontend-homepage.staging.mcd-it.platform-tbwa-x.com/fullscreen=true&appOrientation=portrait'
                : 'https://frontend-homepage.production.mcd-it.platform-tbwa-x.com/fullscreen=true&appOrientation=portrait'
            }
            gtmConfig={{
              eventLabel: '/family-mode/klaar',
              eventAction: 'klaar!',
              event: 'button_click'
            }}
          />
        </ButtonsContainer>
      </PageContainer>

      {errorAddMember && (
        <PopUpErrorAddMember
          onClose={onCloseErrorAddMember}
          campaign={campaign}
        />
      )}

      {errorDeleteMember && (
        <PopUpErrorDeleteMember
          onClose={onCloseErrorDeleteMember}
          campaign={campaign}
        />
      )}

      {deleteMemberId && (
        <PopUpDeleteMember
          onClose={onCloseDeleteMember}
          onDelete={onDeleteMember}
          campaign={campaign}
        />
      )}

      {serverNameError && (
        <ErrorPopup
          error={serverNameError}
          onClose={() => {
            setServerNameError(null);
            setIsEditingName(true);
          }}
          onClick={() => {
            setServerNameError(null);
            setIsEditingName(true);
          }}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: never) => ({
  family: getUserFamily(state),
  campaign: getCampaign(state)
});

export default connect(mapStateToProps)(Overview);
